import _get from 'lodash/get'
import { List, Map } from 'immutable'
import { selectGiftPlanByPlanId } from 'services/plans/selectors'
import { URL_GET_STARTED, URL_HOME } from 'services/url/constants'
import HistoryContext from 'components/Router/HistoryContext'
import { TARGET_BLANK } from 'components/Link/constants'
import { dismissModal } from 'services/dialog/actions'
import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get as getConfig } from 'config'
import { useTranslation } from 'services/static-text/hooks'
import Button, { TYPES } from 'components/Button'
import _toString from 'lodash/toString'
import { H6 } from 'components/Heading'
import _replace from 'lodash/replace'
import {
  selectGiftRedeemErrorState,
  selectGiftRedeemErrorData,
  selectGiftRedeemData,
  selectGiftDetails,
} from 'services/gift/selectors'
import { USER_ACCOUNT_CANCELLED } from 'services/user-account'
import { selectAccountStatus } from 'services/user-account/selectors'
import { selectAuth } from 'services/auth/selectors'
import { selectPrimaryLanguage } from 'services/user/selectors'
import { useGetPlanDataBySku } from 'services/plans/hooks'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

function renderMessage ({
  isAccountCancelled,
  redeemErrorState,
  translateByKey,
  errorCode,
  planTitle,
  userIsNew,
}) {
  if (redeemErrorState) {
    const href = `mailto:info@gaia.com?subject=Gift Redemption ${translateByKey(errorCode)}`
    return (
      <p className="modal-gift-confirm__message">
        {translateByKey(`errMsg${errorCode}`)}
        {translateByKey('errMsg2')}
        <a
          className="modal-gift-confirm__link"
          target={TARGET_BLANK}
          href={href}
        >
          info@gaia.com
        </a>
        {translateByKey('errMsg3')}
      </p>
    )
  }

  if (isAccountCancelled) {
    return (
      <p className="modal-gift-confirm__message">
        {
          // eslint-disable-next-line no-template-curly-in-string
          _replace(translateByKey('successMessageCanceledUser'), '${ planTitle }', planTitle)
        }
      </p>
    )
  }

  const successMessageKey = userIsNew ? 'successMessageNewUser' : 'successMessageExistingUser'

  return (
    <p className="modal-gift-confirm__message">
      {
        // eslint-disable-next-line no-template-curly-in-string
        _replace(translateByKey(successMessageKey), '${ planTitle }', planTitle)
      }
    </p>
  )
}

function MessageGraphQL (props) {
  const {
    isAccountCancelled,
    redeemErrorState,
    translateByKey,
    errorCode,
    userIsNew,
    auth,
    userLanguage,
    planSku,
  } = props

  let giftPlan = List()

  if (planSku) {
    const gqlPlanData = useGetPlanDataBySku({
      userLanguage,
      authJwt: auth.get('jwt'),
      planSkus: planSku,
    }) || Map()
    giftPlan = gqlPlanData.get('plans', List()) || List()
  }

  const planTitle = giftPlan.getIn([0, 'title']) || ''

  if (redeemErrorState) {
    const href = `mailto:info@gaia.com?subject=Gift Redemption ${translateByKey(errorCode)}`
    return (
      <p className="modal-gift-confirm__message">
        {translateByKey(`errMsg${errorCode}`)}
        {translateByKey('errMsg2')}
        <a
          className="modal-gift-confirm__link"
          target={TARGET_BLANK}
          href={href}
        >
          info@gaia.com
        </a>
        {translateByKey('errMsg3')}
      </p>
    )
  }

  if (isAccountCancelled) {
    return (
      <p className="modal-gift-confirm__message">
        {
          // eslint-disable-next-line no-template-curly-in-string
          _replace(translateByKey('successMessageCanceledUser'), '${ planTitle }', planTitle)
        }
      </p>
    )
  }

  const successMessageKey = userIsNew ? 'successMessageNewUser' : 'successMessageExistingUser'

  return (
    <p className="modal-gift-confirm__message">
      {
        // eslint-disable-next-line no-template-curly-in-string
        _replace(translateByKey(successMessageKey), '${ planTitle }', planTitle)
      }
    </p>
  )
}

function getBtnTextKey ({ redeemErrorState, userIsNew }) {
  if (redeemErrorState) return 'closeBtn'
  return userIsNew ? 'startWatchingBtn' : 'homeBtn'
}

export default function ModalGiftConfirm () {
  const history = useContext(HistoryContext)
  const dispatch = useDispatch()
  const redeemErrorState = useSelector(selectGiftRedeemErrorState)
  const translateByKey = useTranslation('modalGiftConfirm')
  const redeemErrorData = useSelector(selectGiftRedeemErrorData)
  const redeemData = useSelector(selectGiftRedeemData)
  const giftDetails = useSelector(selectGiftDetails)
  const planId = giftDetails.get('planId')
  const planSku = giftDetails.get('planSku')
  const plan = useSelector(selectGiftPlanByPlanId(planId))
  const planTitle = plan && plan.get('title', '')
  const userIsNew = redeemData.get('userIsNew', undefined)
  const errorCode = redeemErrorData && _toString(redeemErrorData.get('errorDisplayCode', ''))
  const accountStatus = useSelector(selectAccountStatus)
  const isAccountCancelled = accountStatus === USER_ACCOUNT_CANCELLED
  const auth = useSelector(selectAuth)
  const userLanguage = useSelector(selectPrimaryLanguage)

  const onConfirm = useCallback(() => {
    if (userIsNew) {
      dispatch(dismissModal())
      history.push(URL_GET_STARTED)
      return
    }
    if (redeemErrorState) {
      dispatch(dismissModal())
      return
    }
    dispatch(dismissModal())
    history.push(URL_HOME)
  }, [userIsNew, redeemErrorState, history])

  const titleKey = redeemErrorState ? `errTitle${errorCode}` : 'successTitle'
  const btnKey = getBtnTextKey({
    redeemErrorState,
    userIsNew,
  })

  return (
    <div className="modal-gift-confirm">
      <div className="modal-gift-confirm__wrapper">
        <H6>{translateByKey(titleKey)}</H6>
        <div className="modal-gift-confirm__message">
          {
            planGraphQLActive
              ? <MessageGraphQL
                  auth={auth}
                  userLanguage={userLanguage}
                  isAccountCancelled={isAccountCancelled}
                  redeemErrorState={redeemErrorState}
                  translateByKey={translateByKey}
                  errorCode={errorCode}
                  userIsNew={userIsNew}
                  planSku={planSku}
                />
              : renderMessage({
                isAccountCancelled,
                redeemErrorState,
                translateByKey,
                errorCode,
                planTitle,
                userIsNew,
              })
          }
        </div>
        <Button
          className="modal-gift-confirm__btn"
          type={TYPES.PRIMARY}
          onClick={onConfirm}
        >
          {translateByKey(btnKey)}
        </Button>
      </div>
    </div>
  )
}
